import { queryOptions, skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApplicationDataType } from '@/components/contacts/application-list/application-list-item';
import useFriends from '@/hooks/contacts/use-friends';
import { generateGroupInfoQueryOptions } from '@/hooks/groups/use-group-info';
import { type GroupJoinApplication, groupJoinApplicationScheme } from '@/models/group/group-info';
import {
  generateListResponseScheme,
  type ListRespondedWith,
  type RespondedType,
  type ResponseError,
} from '@/models/response';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';

export const generateGroupJoinApplicationQueryOptions = (groupId: number | null | undefined) =>
  queryOptions({
    queryKey: ['/GetGroupJoinApplications', groupId],
    queryFn: groupId
      ? async ({ signal }) => {
          const response = await fetcher<ListRespondedWith<GroupJoinApplication>>(
            '/GetGroupJoinApplications',
            { GroupId: groupId },
            { signal, schema: generateListResponseScheme(groupJoinApplicationScheme), validResponseCodes: [0, 1] },
          );

          return (
            response.Data?.map<ApplicationDataType>((d) => ({
              id: d.ID,
              userId: d.UserID,
              status: 'pending',
              userName: d.UserNickName,
              avatarUrl: d.UserHeadImg,
              createTime: d.CreateTime,
            })) ?? []
          );
        }
      : skipToken,
    placeholderData: [],
    staleTime: 15 * 1000,
  });

export default function useGroupJoinApplications(groupId: number | null | undefined) {
  const { friends } = useFriends();

  const query = useQuery({
    ...generateGroupJoinApplicationQueryOptions(groupId),
    select: (data) =>
      data.map((d) => {
        const friend = friends[d.id];
        d.userName = friend?.RemarkName || d.userName;
        return d;
      }),
  });

  return {
    groupJoinApplications: query.data ?? [],
    ...query,
  };
}

export function useRespondGroupJoinApplication(groupId: Nullable<number>, application: ApplicationDataType) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, 'accept' | 'reject'>({
    mutationKey: ['/ProcessGroupJoinApplication', groupId, application.id],
    mutationFn: (params) =>
      fetcher(
        '/ProcessGroupJoinApplication',
        {
          GroupID: groupId,
          ApplicationID: application.id,
          Status: params === 'accept' ? '1' : '2',
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: () =>
      Promise.allSettled([
        queryClient.invalidateQueries(generateGroupInfoQueryOptions(groupId)),
        queryClient.refetchQueries(generateGroupJoinApplicationQueryOptions(groupId)),
      ]),
    onSettled: (data, error) =>
      addToast({
        type: data ? 'success' : 'error',
        message: data?.Msg ?? error?.message,
      }),
  });
}
